import React, { useState, useEffect } from 'react';
import { GripVertical, Eye, EyeOff, Save, Home, Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, Sparkles, Tag, Grid, TrendingUp, Layers, Mail, Type, ShoppingBag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { HomepageVisibilityCard } from '../../components/admin/HomepageVisibilityCard';
import { toast } from 'sonner';

// Verfügbare Abschnittstypen
const AVAILABLE_SECTIONS = [
  {
    id: 'hero',
    name: 'Willkommens-Banner',
    description: 'Großes Banner mit Suchleiste',
    icon: ImageIcon,
    color: '#E8D5F0',
    preview: (
      <div className="bg-[#E8E0D5] p-3 h-full flex flex-col justify-center">
        <div className="text-[8px] font-bold text-[#6D5A47]">Willkommen</div>
        <div className="text-[6px] text-[#6D5A47]/70">Entdecken Sie...</div>
        <div className="mt-1 bg-[#C4A084] text-white text-[5px] px-1 py-0.5 w-fit rounded-sm">Button</div>
      </div>
    )
  },
  {
    id: 'seasonal',
    name: 'Saisonale Angebote',
    description: 'Ostern, Weihnachten usw.',
    icon: Sparkles,
    color: '#D5E8F0',
    preview: (
      <div className="bg-[#FFF8E8] p-2 h-full">
        <div className="text-[7px] font-bold text-center">✨ Saisonal</div>
        <div className="flex gap-1 mt-1 justify-center">
          <div className="w-4 h-4 bg-[#C4A084]/30 rounded"></div>
          <div className="w-4 h-4 bg-[#C4A084]/30 rounded"></div>
        </div>
      </div>
    )
  },
  {
    id: 'coupon',
    name: 'Rabatt-Banner',
    description: 'Zeigt Gutscheincode',
    icon: Tag,
    color: '#D5F0E0',
    preview: (
      <div className="bg-[#E8F5E9] p-2 h-full flex items-center justify-center">
        <div className="bg-white px-2 py-1 rounded border border-dashed border-green-400">
          <span className="text-[6px] font-mono font-bold">CODE10</span>
        </div>
      </div>
    )
  },
  {
    id: 'catalogs',
    name: 'Produktkataloge',
    description: 'Kategorien anzeigen',
    icon: Grid,
    color: '#F0E8D5',
    preview: (
      <div className="bg-white p-1 h-full">
        <div className="grid grid-cols-2 gap-0.5">
          <div className="aspect-square bg-[#E8E0D5] rounded-sm"></div>
          <div className="aspect-square bg-[#E8E0D5] rounded-sm"></div>
          <div className="aspect-square bg-[#E8E0D5] rounded-sm"></div>
          <div className="aspect-square bg-[#E8E0D5] rounded-sm"></div>
        </div>
      </div>
    )
  },
  {
    id: 'featured',
    name: 'Beliebte Produkte',
    description: 'Bestseller anzeigen',
    icon: TrendingUp,
    color: '#F0F0D5',
    preview: (
      <div className="bg-[#F5EDE4] p-1 h-full">
        <div className="text-[6px] font-bold mb-1">Beliebt</div>
        <div className="flex gap-0.5">
          <div className="w-5 h-6 bg-white rounded-sm shadow-sm"></div>
          <div className="w-5 h-6 bg-white rounded-sm shadow-sm"></div>
          <div className="w-5 h-6 bg-white rounded-sm shadow-sm"></div>
        </div>
      </div>
    )
  },
  {
    id: 'categories',
    name: 'Kategorien-Kacheln',
    description: 'Schnellzugriff',
    icon: Layers,
    color: '#E0D5F0',
    preview: (
      <div className="bg-white p-1 h-full flex gap-0.5 items-center justify-center">
        <div className="px-1 py-0.5 bg-[#C4A084]/20 rounded-full text-[5px]">Kat 1</div>
        <div className="px-1 py-0.5 bg-[#C4A084]/20 rounded-full text-[5px]">Kat 2</div>
      </div>
    )
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'E-Mail Anmeldung',
    icon: Mail,
    color: '#F0D5E0',
    preview: (
      <div className="bg-[#F5EDE4] p-2 h-full flex flex-col items-center justify-center">
        <Mail className="h-3 w-3 text-[#C4A084] mb-1" />
        <div className="text-[5px]">Newsletter</div>
      </div>
    )
  },
  {
    id: 'text_block',
    name: 'Text-Bereich',
    description: 'Freier Text/Info',
    icon: Type,
    color: '#E8E8E8',
    preview: (
      <div className="bg-white p-2 h-full">
        <div className="space-y-0.5">
          <div className="h-1 bg-gray-300 rounded w-full"></div>
          <div className="h-1 bg-gray-200 rounded w-3/4"></div>
          <div className="h-1 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    )
  },
  {
    id: 'products_slider',
    name: 'Produkt-Slider',
    description: 'Horizontale Produktliste',
    icon: ShoppingBag,
    color: '#D5F0F0',
    preview: (
      <div className="bg-[#F5F5F5] p-1 h-full">
        <div className="flex gap-0.5 overflow-hidden">
          <div className="w-5 h-7 bg-white rounded-sm shadow-sm flex-shrink-0"></div>
          <div className="w-5 h-7 bg-white rounded-sm shadow-sm flex-shrink-0"></div>
          <div className="w-5 h-7 bg-white rounded-sm shadow-sm flex-shrink-0"></div>
          <div className="w-5 h-7 bg-white rounded-sm shadow-sm flex-shrink-0 opacity-50"></div>
        </div>
      </div>
    )
  }
];

const HomepageManager = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/homepage-sections/admin/config`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const sectionsArray = data.sections || [];
        setSections(sectionsArray.sort((a, b) => a.order_index - b.order_index));
      } else {
        // Default sections
        setSections([
          { id: 'hero', name: 'Willkommens-Banner', is_active: true, order_index: 0 },
          { id: 'seasonal', name: 'Saisonale Angebote', is_active: true, order_index: 1 },
          { id: 'catalogs', name: 'Produktkataloge', is_active: true, order_index: 2 },
          { id: 'featured', name: 'Beliebte Produkte', is_active: true, order_index: 3 },
          { id: 'newsletter', name: 'Newsletter', is_active: true, order_index: 4 }
        ]);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/homepage-sections/admin/config`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ sections })
        }
      );

      if (response.ok) {
        toast.success('Gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionId) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, is_active: !section.is_active }
        : section
    ));
  };

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const newSections = [...sections];
    const temp = newSections[index];
    newSections[index] = newSections[newIndex];
    newSections[newIndex] = temp;
    
    // Update order indices
    newSections.forEach((section, idx) => {
      section.order_index = idx;
    });
    
    setSections(newSections);
    toast.success('Reihenfolge geändert');
  };

  const addSection = (sectionType) => {
    const sectionInfo = AVAILABLE_SECTIONS.find(s => s.id === sectionType);
    if (!sectionInfo) return;

    // Check if section already exists
    if (sections.find(s => s.id === sectionType)) {
      toast.error('Dieser Abschnitt ist bereits vorhanden');
      return;
    }

    const newSection = {
      id: sectionType,
      name: sectionInfo.name,
      is_active: true,
      order_index: sections.length
    };

    setSections([...sections, newSection]);
    setShowAddDialog(false);
    toast.success(`"${sectionInfo.name}" hinzugefügt`);
  };

  const removeSection = (sectionId) => {
    if (!window.confirm('Abschnitt wirklich entfernen?')) return;
    
    const newSections = sections.filter(s => s.id !== sectionId);
    newSections.forEach((section, idx) => {
      section.order_index = idx;
    });
    
    setSections(newSections);
    toast.success('Abschnitt entfernt');
  };

  const getSectionInfo = (sectionId) => {
    return AVAILABLE_SECTIONS.find(s => s.id === sectionId) || {
      name: sectionId,
      description: '',
      icon: Layers,
      color: '#E8E8E8',
      preview: <div className="bg-gray-100 h-full"></div>
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8B7355] mx-auto mb-3"></div>
          <p className="text-[#8B7355]">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-3xl mx-auto">
        <AdminPageHeader 
          title="Homepage verwalten" 
          subtitle="Bereiche ein-/ausblenden und Reihenfolge ändern"
          icon={Home}
        />

        <HomepageVisibilityCard />

        {/* Anleitung */}
        <div className="mb-4 p-3 rounded-none" style={{ backgroundColor: '#E8F4E8', border: '1px solid #90C090' }}>
          <p className="text-sm text-green-800">
            <strong>So geht's:</strong> Schalten Sie Bereiche mit dem Schalter ein/aus. 
            Nutzen Sie die <strong>Pfeile ▲ ▼</strong> um die Reihenfolge zu ändern. 
            Die <strong>Vorschau</strong> zeigt wie jeder Bereich aussieht.
          </p>
        </div>

        {/* Neuen Abschnitt hinzufügen */}
        <Button 
          onClick={() => setShowAddDialog(true)}
          className="w-full mb-4 rounded-none"
          style={{ backgroundColor: '#AF9774' }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Neuen Abschnitt hinzufügen
        </Button>

        {/* Sektions-Liste mit Vorschau */}
        <div className="space-y-3">
          {sections.map((section, index) => {
            const info = getSectionInfo(section.id);
            const SectionIcon = info.icon;
            
            return (
              <Card
                key={section.id}
                className={`rounded-none border transition-all ${!section.is_active ? 'opacity-60' : ''}`}
                style={{ borderColor: '#C4A084' }}
              >
                <CardContent className="p-0">
                  <div className="flex">
                    {/* Vorschau links */}
                    <div 
                      className="w-20 h-20 flex-shrink-0 border-r overflow-hidden"
                      style={{ borderColor: '#C4A084' }}
                      title="Vorschau"
                    >
                      {info.preview}
                    </div>

                    {/* Hauptinhalt */}
                    <div className="flex-1 p-3">
                      <div className="flex items-center gap-2">
                        {/* Nummer */}
                        <div 
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: '#AF9774' }}
                        >
                          {index + 1}
                        </div>

                        {/* Icon */}
                        <div 
                          className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: info.color }}
                        >
                          <SectionIcon className="h-4 w-4 text-gray-700" />
                        </div>

                        {/* Name */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-gray-800 truncate">
                            {info.name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {info.description}
                          </p>
                        </div>

                        {/* Verschieben-Buttons */}
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveSection(index, -1)}
                            disabled={index === 0}
                            className="w-7 h-7 flex items-center justify-center rounded bg-gray-100 hover:bg-[#AF9774] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-100 disabled:hover:text-gray-700 transition-colors"
                            title="Nach oben"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => moveSection(index, 1)}
                            disabled={index === sections.length - 1}
                            className="w-7 h-7 flex items-center justify-center rounded bg-gray-100 hover:bg-[#AF9774] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-100 disabled:hover:text-gray-700 transition-colors"
                            title="Nach unten"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Ein/Aus Schalter */}
                        <div className="flex flex-col items-center gap-1">
                          <Switch
                            checked={section.is_active}
                            onCheckedChange={() => toggleSection(section.id)}
                          />
                          <span className="text-[10px]">
                            {section.is_active ? (
                              <span className="text-green-600 font-medium">An</span>
                            ) : (
                              <span className="text-gray-400">Aus</span>
                            )}
                          </span>
                        </div>

                        {/* Löschen */}
                        <button
                          onClick={() => removeSection(section.id)}
                          className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                          title="Entfernen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Speichern Button */}
        <div className="mt-6">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full rounded-none"
            style={{ backgroundColor: '#AF9774' }}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Speichern...' : 'Änderungen speichern'}
          </Button>
        </div>

        {/* Dialog: Neuen Abschnitt hinzufügen */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Neuen Abschnitt hinzufügen</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-2 mt-4">
              {AVAILABLE_SECTIONS.map((sectionType) => {
                const alreadyExists = sections.find(s => s.id === sectionType.id);
                const SectionIcon = sectionType.icon;
                
                return (
                  <button
                    key={sectionType.id}
                    onClick={() => !alreadyExists && addSection(sectionType.id)}
                    disabled={alreadyExists}
                    className={`w-full flex items-center gap-3 p-3 border rounded-none transition-all ${
                      alreadyExists 
                        ? 'opacity-50 cursor-not-allowed bg-gray-50' 
                        : 'hover:border-[#AF9774] hover:bg-[#F5EDE4]'
                    }`}
                  >
                    {/* Vorschau */}
                    <div className="w-12 h-12 flex-shrink-0 border rounded overflow-hidden">
                      {sectionType.preview}
                    </div>
                    
                    {/* Icon & Text */}
                    <div 
                      className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: sectionType.color }}
                    >
                      <SectionIcon className="h-5 w-5 text-gray-700" />
                    </div>
                    
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-sm">{sectionType.name}</h4>
                      <p className="text-xs text-gray-500">{sectionType.description}</p>
                    </div>
                    
                    {alreadyExists && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        Bereits aktiv
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default HomepageManager;
